import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './StaticPages.css';

const API = process.env.REACT_APP_API_URL || '/api';

interface DeliveryRow { location: string; location_bn?: string; time: string; time_bn?: string; notes: string; notes_bn?: string; }
interface CostCard { icon: string; title: string; title_bn?: string; description: string; description_bn?: string; }

interface ShippingData {
  hero_title: string; hero_title_bn?: string;
  hero_subtitle: string; hero_subtitle_bn?: string;
  overview_title: string; overview_title_bn?: string;
  overview_content: string; overview_content_bn?: string;
  overview_note: string; overview_note_bn?: string;
  delivery_title: string; delivery_title_bn?: string;
  delivery_table: DeliveryRow[];
  costs_title: string; costs_title_bn?: string;
  costs_content: string; costs_content_bn?: string;
  costs_cards: CostCard[];
  tracking_title: string; tracking_title_bn?: string;
  tracking_content: string; tracking_content_bn?: string;
  issues_title: string; issues_title_bn?: string;
  issues_content: string; issues_content_bn?: string;
}

const ShippingPolicy: React.FC = () => {
  const { i18n } = useTranslation();
  const isBn = i18n.language === 'bn';
  const [cms, setCms] = useState<ShippingData | null>(null);

  useEffect(() => {
    fetch(`${API}/pages/shipping/`)
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setCms(d))
      .catch(() => {});
  }, []);

  const t = (en: string | undefined, bn: string | undefined, fallback: string) =>
    isBn ? (bn || en || fallback) : (en || fallback);

  const d = cms;
  const hero_title = t(d?.hero_title, d?.hero_title_bn, 'Shipping Policy');
  const hero_subtitle = t(d?.hero_subtitle, d?.hero_subtitle_bn, 'Everything you need to know about how we ship your orders safely and on time.');
  const overview_title = t(d?.overview_title, d?.overview_title_bn, 'How We Ship');
  const overview_content = t(d?.overview_content, d?.overview_content_bn, 'We are committed to delivering your orders quickly, safely, and reliably.');
  const overview_note = t(d?.overview_note, d?.overview_note_bn, 'Delivery times are estimates and may vary due to weather, public holidays, or courier delays.');
  const delivery_title = t(d?.delivery_title, d?.delivery_title_bn, 'Estimated Delivery');
  const delivery_table = d?.delivery_table || [
    { location: 'Inside Dhaka', time: '1\u20132 Business Days', notes: 'Same-day dispatch for orders placed before 12 PM' },
    { location: 'Outside Dhaka', time: '3\u20135 Business Days', notes: 'Remote areas may take an additional 1\u20132 days' },
    { location: 'Remote Areas', time: '5\u20137 Business Days', notes: 'Contact us for availability in your area' },
  ];
  const costs_title = t(d?.costs_title, d?.costs_title_bn, 'Delivery Charges');
  const costs_content = t(d?.costs_content, d?.costs_content_bn, 'Shipping fees are calculated at checkout based on your delivery address.');
  const costs_cards = d?.costs_cards || [
    { icon: '\uD83C\uDFD9\uFE0F', title: 'Inside Dhaka', description: 'Standard delivery fee applies.' },
    { icon: '\uD83D\uDDFA\uFE0F', title: 'Outside Dhaka', description: 'Slightly higher courier charge for district-level deliveries.' },
    { icon: '\uD83C\uDF81', title: 'Free Shipping', description: 'Eligible on select orders or promotions.' },
  ];
  const tracking_title = t(d?.tracking_title, d?.tracking_title_bn, 'Track Your Order');
  const tracking_content = t(d?.tracking_content, d?.tracking_content_bn, "Once your order is dispatched, you'll receive a tracking number via SMS.");
  const issues_title = t(d?.issues_title, d?.issues_title_bn, 'Lost or Damaged Packages');
  const issues_content = t(d?.issues_content, d?.issues_content_bn, 'If your package arrives damaged or goes missing, please contact us within 48 hours.');

  return (
    <div className="static-page">
      <div className="static-hero">
        <h1>{hero_title}</h1>
        <p>{hero_subtitle}</p>
      </div>

      <div className="static-content">
        {/* Overview */}
        <div className="static-section">
          <span className="section-tag">{isBn ? '\u09B8\u09BE\u09B0\u09B8\u0982\u0995\u09CD\u09B7\u09C7\u09AA' : 'Overview'}</span>
          <h2>{overview_title}</h2>
          <p>{overview_content}</p>
          {overview_note && (
            <div className="policy-callout"><strong>{isBn ? '\u09A8\u09CB\u099F:' : 'Note:'}</strong> {overview_note}</div>
          )}
        </div>

        <hr className="static-divider" />

        {/* Delivery Times */}
        <div className="static-section">
          <span className="section-tag">{isBn ? '\u09A1\u09C7\u09B2\u09BF\u09AD\u09BE\u09B0\u09BF \u09B8\u09AE\u09AF\u09BC' : 'Delivery Times'}</span>
          <h2>{delivery_title}</h2>
          <div className="table-wrapper">
            <table className="static-table">
              <thead><tr>
                <th>{isBn ? '\u09B2\u09CB\u0995\u09C7\u09B6\u09A8' : 'Location'}</th>
                <th>{isBn ? '\u09A1\u09C7\u09B2\u09BF\u09AD\u09BE\u09B0\u09BF \u09B8\u09AE\u09AF\u09BC' : 'Delivery Time'}</th>
                <th>{isBn ? '\u09A8\u09CB\u099F' : 'Notes'}</th>
              </tr></thead>
              <tbody>
                {delivery_table.map((row, i) => (
                  <tr key={i}>
                    <td><strong>{isBn ? (row.location_bn || row.location) : row.location}</strong></td>
                    <td>{isBn ? (row.time_bn || row.time) : row.time}</td>
                    <td>{isBn ? (row.notes_bn || row.notes) : row.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <hr className="static-divider" />

        {/* Shipping Costs */}
        <div className="static-section">
          <span className="section-tag">{isBn ? '\u09B6\u09BF\u09AA\u09BF\u0982 \u0996\u09B0\u099A' : 'Shipping Costs'}</span>
          <h2>{costs_title}</h2>
          <p>{costs_content}</p>
          <div className="info-grid">
            {costs_cards.map((card, i) => (
              <div className="static-info-card" key={i}>
                <div className="static-info-card-icon">{card.icon}</div>
                <h4>{isBn ? (card.title_bn || card.title) : card.title}</h4>
                <p>{isBn ? (card.description_bn || card.description) : card.description}</p>
              </div>
            ))}
          </div>
        </div>

        <hr className="static-divider" />

        {/* Order Tracking */}
        <div className="static-section">
          <span className="section-tag">{isBn ? '\u099F\u09CD\u09B0\u09CD\u09AF\u09BE\u0995\u09BF\u0982' : 'Tracking'}</span>
          <h2>{tracking_title}</h2>
          <p>{tracking_content}</p>
          <div className="static-card">
            <h3>{isBn ? '\uD83D\uDCE6 \u09B0\u09BF\u09AF\u09BC\u09C7\u09B2-\u099F\u09BE\u0987\u09AE \u0985\u09B0\u09CD\u09A1\u09BE\u09B0 \u099F\u09CD\u09B0\u09CD\u09AF\u09BE\u0995\u09BF\u0982' : '\uD83D\uDCE6 Real-Time Order Tracking'}</h3>
            <p>
              {isBn ? '\u0986\u09AE\u09BE\u09A6\u09C7\u09B0 ' : 'Visit our '}
              <Link to="/track-order" style={{ color: 'var(--primary)', fontWeight: 600 }}>
                {isBn ? '\u0985\u09B0\u09CD\u09A1\u09BE\u09B0 \u099F\u09CD\u09B0\u09CD\u09AF\u09BE\u0995' : 'Track Order'}
              </Link>
              {isBn ? ' \u09AA\u09C7\u099C\u09C7 \u09AF\u09BE\u09A8 \u098F\u09AC\u0982 \u0986\u09AA\u09A8\u09BE\u09B0 \u0985\u09B0\u09CD\u09A1\u09BE\u09B0 \u09A8\u09AE\u09CD\u09AC\u09B0 \u09A6\u09BF\u09A8\u0964' : ' page and enter your order number to see the current status of your delivery.'}
            </p>
          </div>
        </div>

        <hr className="static-divider" />

        {/* Issues */}
        <div className="static-section">
          <span className="section-tag">{isBn ? '\u09B8\u09AE\u09B8\u09CD\u09AF\u09BE' : 'Issues'}</span>
          <h2>{issues_title}</h2>
          {issues_content.split('\n').filter(Boolean).map((p, i) => <p key={i} style={i > 0 ? { marginTop: 14 } : undefined}>{p}</p>)}
          <div className="static-card" style={{ marginTop: 20 }}>
            <h3>{isBn ? '\u09B8\u09BE\u09B9\u09BE\u09AF\u09CD\u09AF \u09AA\u09CD\u09B0\u09AF\u09BC\u09CB\u099C\u09A8?' : 'Need Help?'}</h3>
            <p>
              {isBn ? '\u0986\u09AE\u09BE\u09A6\u09C7\u09B0 ' : 'Contact our support team via the '}
              <Link to="/contact" style={{ color: 'var(--primary)', fontWeight: 600 }}>
                {isBn ? '\u09AF\u09CB\u0997\u09BE\u09AF\u09CB\u0997' : 'Contact Us'}
              </Link>
              {isBn ? ' \u09AA\u09C7\u099C\u09C7 \u09AF\u09CB\u0997\u09BE\u09AF\u09CB\u0997 \u0995\u09B0\u09C1\u09A8\u0964' : ' page and we\'ll resolve it as quickly as possible.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShippingPolicy;
